import {
  activateDrawer,
  afterDrawerClose,
  lockScroll,
  bindDrawerEscape,
  releaseLockedScroll,
} from '@theme/coco-utilities';

const DESIGN_MODE_BOUND = { value: false };
const DOB_MAX_DIGITS = 8;
const DOB_FORMAT_LENGTH = 10;
const DOB_PLACEHOLDER = 'DD/MM/YYYY';
const DOB_LAST_YEAR = 1900;

function formatDobDigits(digits) {
  const value = digits.replace(/\D/g, '').slice(0, DOB_MAX_DIGITS);
  if (value.length <= 2) return value;
  if (value.length <= 4) return `${value.slice(0, 2)}/${value.slice(2)}`;
  return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
}

function applyDobMask(input) {
  if (!input) return;

  const selectionStart = input.selectionStart ?? input.value.length;
  const digitsBeforeCursor = input.value.slice(0, selectionStart).replace(/\D/g, '').length;
  const formatted = formatDobDigits(input.value);

  input.value = formatted;

  let newPos = formatted.length;
  let digitCount = 0;

  for (let i = 0; i < formatted.length; i++) {
    if (!/\d/.test(formatted[i])) continue;
    digitCount += 1;
    newPos = i + 1;
    if (digitCount >= digitsBeforeCursor) break;
  }

  input.setSelectionRange(newPos, newPos);
}

function getAgeVerificationPopup(id) {
  if (id) return document.getElementById(id);
  return document.querySelector('[is="age-verification"]');
}

function getModalForSection(sectionId) {
  if (!sectionId) return null;
  return document.querySelector(
    `[is="age-verification"][data-section-id="${CSS.escape(String(sectionId))}"]`
  );
}

function removeStaleModals(keep = null) {
  document.querySelectorAll('[is="age-verification"]').forEach((modal) => {
    if (modal === keep) return;
    modal.forceClose?.();
    modal.remove();
  });
}

function bindDesignModeEvents() {
  if (DESIGN_MODE_BOUND.value) return;
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  DESIGN_MODE_BOUND.value = true;

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    removeStaleModals();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    requestAnimationFrame(() => {
      const modal = getModalForSection(sectionId);
      if (!modal) return;

      removeStaleModals(modal);
      modal.show();
    });
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    getModalForSection(sectionId)?.forceClose?.();
  });
}

class AgeVerification extends HTMLDivElement {
  constructor() {
    super();
    this.activeElement = null;
    this.releaseModal = null;
    this.loadTimeout = null;
    this.closeTimeout = null;
    this.onClick = this.onClick.bind(this);
    this.onDobInput = this.onDobInput.bind(this);
    this.onDobKeydown = this.onDobKeydown.bind(this);
    this.unbindEscape = null;
  }

  get isDesignMode() {
    return typeof Shopify !== 'undefined' && Shopify.designMode;
  }

  get shouldLock() {
    return !this.isDesignMode;
  }

  get testMode() {
    return this.dataset.testMode === 'true';
  }

  get delay() {
    return 1;
  }

  get expiry() {
    return parseInt(this.dataset.expiry, 10) || 30;
  }

  get cookieName() {
    return 'coco:age-verifier';
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get minimumAge() {
    const fromDob = parseInt(this.dobField?.dataset.minimumAge, 10);
    return Number.isFinite(fromDob) ? fromDob : 18;
  }

  get dobField() {
    return this.querySelector('[data-age-verification-dob]');
  }

  connectedCallback() {
    removeStaleModals(this);

    this.addEventListener('click', this.onClick);
    this.unbindEscape = bindDrawerEscape(this, () => this.hide());
    this.bindDobInput();
    this.moveToBody();
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.unbindEscape?.();
    this.unbindEscape = null;
    this.unbindDobInput();
    clearTimeout(this.loadTimeout);
    this.forceClose();
  }

  bindDobInput() {
    const input = this.dobField?.querySelector('[data-dob-day]');
    if (!input) return;

    input.setAttribute('maxlength', String(DOB_FORMAT_LENGTH));
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('pattern', '[0-9]{2}/[0-9]{2}/[0-9]{4}');
    input.removeEventListener('input', this.onDobInput);
    input.removeEventListener('keydown', this.onDobKeydown);
    input.addEventListener('input', this.onDobInput);
    input.addEventListener('keydown', this.onDobKeydown);
  }

  unbindDobInput() {
    const input = this.dobField?.querySelector('[data-dob-day]');
    if (!input) return;

    input.removeEventListener('input', this.onDobInput);
    input.removeEventListener('keydown', this.onDobKeydown);
  }

  onDobInput(event) {
    applyDobMask(event.target);
    this.clearDobError();
  }

  onDobKeydown(event) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const digits = event.target.value.replace(/\D/g, '');
    if (digits.length >= DOB_MAX_DIGITS) {
      event.preventDefault();
    }
  }

  init() {
    bindDesignModeEvents();

    if (this.isDesignMode) {
      if (this.testMode) this.load(0);
      return;
    }

    if (this.testMode) return;

    if (!this.hasAgeCookie()) {
      this.load(this.delay);
    }
  }

  hasAgeCookie() {
    return Boolean(this.getCookie(this.cookieName));
  }

  load(delay) {
    if (this.isDesignMode) return;

    clearTimeout(this.loadTimeout);
    this.loadTimeout = setTimeout(() => this.show(), delay * 1000);
  }

  moveToBody() {
    if (this.parentElement === document.body) return;
    document.body.appendChild(this);
  }

  forceClose() {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = null;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = null;

    if (this.releaseModal) {
      this.releaseModal();
      this.releaseModal = null;
    }

    this.removeAttribute('active');
    this.removeAttribute('open');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('show-image');
    this.activeElement = null;

    releaseLockedScroll(this);
  }

  onClick(event) {
    if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

    const action = event.target.closest('[data-verification-type]');
    const primaryButton = event.target.closest('.button-primary');

    if (primaryButton && action) {
      event.preventDefault();

      if (action?.dataset.verificationType === 'dob' && !this.validateDob()) return;

      this.hide();
    }
  }

  show(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    this.releaseModal?.();
    this.releaseModal = null;

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('open', '');
    requestAnimationFrame(() => this.setAttribute('active', ''));
    if (this.shouldLock) {
      lockScroll(this);
      this.setAttribute('data-scroll-locked', '');
    }

    if (triggeredBy) triggeredBy.setAttribute('aria-expanded', 'true');

    if (!this.isDesignMode) {
      this.releaseModal = activateDrawer(this, {
        initialFocus: (drawer) =>
          drawer.querySelector('[is="close-drawer"]:not([data-fixed-overlay])') || drawer,
        trap: true,
      });
    }

    this.afterShow();
  }

  hide() {
    if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

    this.classList.remove('show-image');
    this.removeAttribute('active');

    const trigger =
      this.activeElement?.isConnected
        ? this.activeElement
        : document.querySelector(`[data-age-verification-open][aria-controls="${this.id}"]`);
    trigger?.setAttribute?.('aria-expanded', 'false');

    const releaseModal = this.releaseModal;
    this.releaseModal = null;
    this.activeElement = null;
    releaseModal?.(trigger);

    const finish = () => {
      if (this.hasAttribute('active')) return;

      this.removeAttribute('open');
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      releaseLockedScroll(this);
      this.afterHide();
    };

    if (this.isDesignMode) {
      finish();
      return;
    }

    clearTimeout(this.closeTimeout);
    afterDrawerClose(this, () => {
      this.closeTimeout = null;
      finish();
    });
  }

  afterShow() {
    this.classList.add('show-image');
  }

  afterHide() {
    this.clearDobError();

    if (this.isDesignMode) return;

    if (this.testMode) {
      this.removeCookie(this.cookieName);
      document.dispatchEvent(new CustomEvent('coco:age-verification:dismissed'));
      return;
    }

    this.setCookie(this.cookieName, this.expiry);
    document.dispatchEvent(new CustomEvent('coco:age-verification:dismissed'));
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/; SameSite=Lax`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/`;
  }

  showDobError(message) {
    const error = this.querySelector('[data-dob-error]');
    if (!error) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  clearDobError() {
    const error = this.querySelector('[data-dob-error]');
    if (!error) return;
    error.textContent = '';
    error.classList.add('hidden');
  }

  validateDob() {
    const dobField = this.dobField;
    if (!dobField) return true;

    const dobInput = dobField.querySelector('[data-dob-day]');
    const rawValue = (dobInput?.value || '').trim();
    const match = rawValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (!match) {
      this.showDobError('Please enter your date of birth in correct format.');
      return false;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    if (year < DOB_LAST_YEAR) {
      this.showDobError(`Please enter a valid date of birth.`);
      return false;
    }

    const birthDate = new Date(year, month - 1, day);
    if (
      birthDate.getFullYear() !== year ||
      birthDate.getMonth() !== month - 1 ||
      birthDate.getDate() !== day
    ) {
      this.showDobError('Please enter a valid date of birth.');
      return false;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const hadBirthday =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    if (!hadBirthday) age -= 1;

    if (age < this.minimumAge) {
      this.showDobError(`You must be at least ${this.minimumAge} years old.`);
      return false;
    }

    if (dobInput) {
      dobInput.value = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }

    this.clearDobError();
    return true;
  }
}


if (!customElements.get('age-verification')) {
  customElements.define('age-verification', AgeVerification, { extends: 'div' });

}
